import subscriptionApi from '@/helpers/subscriptionApi';
import { translateApiError } from '@/utils/serverMessages';

// Grace period configuration (hours)
// This should match the backend ConfigService value for "subscription.grace_period_hours"
const GRACE_PERIOD_HOURS = 24;

export const state = {
  plans: [],
  currentSubscription: null,
  usage: null,
  paymentHistory: [],
  paymentStatistics: null,
  paymentMethods: [],
  loading: false,
  error: null,
  successMessage: null
};

export const mutations = {
  SET_PLANS(state, plans) {
    state.plans = plans;
  },
  SET_USAGE(state, usage) {
    state.usage = usage;
  },
  SET_CURRENT_SUBSCRIPTION(state, subscription) {
    state.currentSubscription = subscription;
  },
  SET_PAYMENT_HISTORY(state, history) {
    state.paymentHistory = history;
  },
  SET_PAYMENT_STATISTICS(state, statistics) {
    state.paymentStatistics = statistics;
  },
  SET_PAYMENT_METHODS(state, methods) {
    state.paymentMethods = methods;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  SET_SUCCESS_MESSAGE(state, message) {
    state.successMessage = message;
  },
  CLEAR_SUCCESS_MESSAGE(state) {
    state.successMessage = null;
  }
};

export const actions = {
  async fetchPlans({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.getSubscriptionPlans();
      commit('SET_PLANS', data);
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to fetch subscription plans'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchUsage({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.getLimits();
      
      // Transform the data if needed to ensure compatibility with existing components
      let transformedData = data;
      
      // If data doesn't have the expected unified structure, try to transform it
      if (!data.monthly_limits && !data.resource_limits && data.usage && data.limits) {
        // Transform old format to new unified format for backward compatibility
        transformedData = {
          ...data,
          monthly_limits: {
            message_generations_used: data.usage.message_generation || 0,
            max_message_generations_per_month: data.limits.max_message_generations_per_month || -1,
            product_imports_used: data.usage.product_import || 0,
            max_product_imports_per_month: data.limits.max_product_imports_per_month || -1
          },
          resource_limits: {
            communities_used: data.usage.communities_used || 0,
            max_communities: data.limits.max_communities || -1,
            groups_used: data.usage.groups_used || 0,
            max_groups: data.limits.max_groups || -1,
            whatsapp_instances_used: data.usage.whatsapp_instances_used || 0,
            max_whatsapp_instances: data.limits.max_whatsapp_instances || -1
          },
          daily_limits: {
            messages_sent: data.usage.messages_sent || 0,
            max_messages_per_day: data.limits.max_messages_per_day || -1,
            product_imports: data.usage.daily_product_imports || 0,
            max_product_imports_per_day: data.limits.max_product_imports_per_day || -1
          },
          feature_access: data.feature_access || {},
          reset_dates: data.reset_dates || {}
        };
      }
      
      commit('SET_USAGE', transformedData);
      return transformedData;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to fetch usage data'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchCurrentSubscription({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.getUserSubscription();
      // data will be null if user has no subscription (404), which is normal
      commit('SET_CURRENT_SUBSCRIPTION', data);
      return data;
    } catch (error) {
      // Only commit error for actual errors, not for "no subscription found"
      commit('SET_ERROR', translateApiError(error, 'Failed to fetch current subscription'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createCheckout({ commit }, planId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.createCheckoutSession(planId);
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to create checkout session'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // DEPRECATED: createDirectSubscription removed - use upgradeTrial instead
  // This action called the deprecated createDirectSubscription API function
  // which was part of the old Sumit-managed subscription system.
  // All subscription creation now goes through the trial upgrade flow.

  async reactivateSubscription({ commit, dispatch }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.reactivateSubscription();
      // Refresh subscription data after reactivation
      await dispatch('fetchCurrentSubscription');
      
      // Show success message
      commit('SET_SUCCESS_MESSAGE', data.message || 'Subscription reactivated successfully');
      
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to reactivate subscription'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async changePlan({ commit, dispatch }, planId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.changePlan(planId);
      // Refresh subscription data after successful plan change
      await dispatch('fetchCurrentSubscription');
      
      // Show success message (handles both reactivation and plan changes)
      commit('SET_SUCCESS_MESSAGE', data.message || 'Subscription updated successfully');
      
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to change subscription plan'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getCustomerPortal({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.getCustomerPortal();
      // Open the customer portal in a new tab
      window.open(data.portal_url, '_blank');
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to get customer portal'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async cancelSubscription({ commit, dispatch }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.cancelSubscription();
      // Refresh subscription data after cancellation
      await dispatch('fetchCurrentSubscription');
      
      // Show success message with access information
      if (data && data.access_until) {
        const accessDate = new Date(data.access_until).toLocaleDateString();
        commit('SET_SUCCESS_MESSAGE', `${data.message} Access until: ${accessDate}`);
      } else {
        commit('SET_SUCCESS_MESSAGE', data.message || 'Subscription canceled successfully');
      }
      
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to cancel subscription'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async handleSubscriptionSuccess({ commit, dispatch }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.getSubscriptionSuccess();
      // Refresh subscription data after successful payment
      await dispatch('fetchCurrentSubscription');
      return data;
    } catch (error) {
      console.error('handleSubscriptionSuccess error:', error);
      
      // Translate the structured server payload when present; error.message
      // is the English fallback (see wrapApiError / translateApiError).
      const errorMessage = translateApiError(error, 'Failed to confirm subscription success');
      commit('SET_ERROR', errorMessage);
      
      // Re-throw the error with proper structure
      throw {
        message: errorMessage,
        status: error?.status || 0,
        details: error?.details || error
      };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchPaymentHistory({ commit }, params = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.getPaymentHistory(params);
      commit('SET_PAYMENT_HISTORY', data);
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to fetch payment history'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchPaymentStatistics({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.getPaymentStatistics();
      commit('SET_PAYMENT_STATISTICS', data);
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to fetch payment statistics'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getTransactionDetails({ commit }, transactionId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.getTransactionDetails(transactionId);
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to fetch transaction details'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // DEPRECATED: createSubscription removed - use upgradeTrial instead
  // This action called the deprecated createDirectSubscription API function
  // which was part of the old Sumit-managed subscription system.
  // All subscription creation now goes through the trial upgrade flow.

  clearSuccessMessage({ commit }) {
    commit('CLEAR_SUCCESS_MESSAGE');
  },

  // Self-Managed Subscription Actions

  async getSubscriptionStatus({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.getSubscriptionStatus();
      commit('SET_CURRENT_SUBSCRIPTION', data);
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to get subscription status'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // async storePaymentMethod({ commit }, token) {
  //   commit('SET_LOADING', true);
  //   commit('CLEAR_ERROR');
  //   try {
  //     const data = await subscriptionApi.storePaymentMethod(token);
  //     return data;
  //   } catch (error) {
  //     commit('SET_ERROR', translateApiError(error, 'Failed to store payment method'));
  //     throw error;
  //   } finally {
  //     commit('SET_LOADING', false);
  //   }
  // },

  // async upgradeTrial({ commit, dispatch }, { plan_id, token }) {
  //   commit('SET_LOADING', true);
  //   commit('CLEAR_ERROR');
  //   try {
  //     const data = await subscriptionApi.upgradeTrial(plan_id, token);
  //     // Refresh subscription data after successful upgrade
  //     await dispatch('fetchCurrentSubscription');
      
  //     // Show success message
  //     commit('SET_SUCCESS_MESSAGE', data.message || 'Trial upgraded successfully');
      
  //     return data;
  //   } catch (error) {
  //     commit('SET_ERROR', translateApiError(error, 'Failed to upgrade trial'));
  //     throw error;
  //   } finally {
  //     commit('SET_LOADING', false);
  //   }
  // },

  async changePlanProrated({ commit, dispatch }, planId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.changePlanProrated(planId);
      // Refresh subscription data after successful plan change
      await dispatch('fetchCurrentSubscription');
      
      // Show success message with proration info
      let message = data.message || 'Plan changed successfully';
      if (data.proration_charge && data.proration_charge > 0) {
        message += ` (Additional charge: $${data.proration_charge.toFixed(2)})`;
      }
      commit('SET_SUCCESS_MESSAGE', message);
      
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to change plan'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getBillingHistory({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.getBillingHistory();
      commit('SET_PAYMENT_HISTORY', data);
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to get billing history'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async storePaymentMethod({ commit }, { token }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.storePaymentMethod({ token });
      commit('SET_SUCCESS_MESSAGE', 'Payment method stored successfully');
      // refresh list of payment methods
      try {
        const methods = await subscriptionApi.listPaymentMethods();
        commit('SET_PAYMENT_METHODS', methods);
      } catch (e) {
        console.error('Failed to refresh payment methods:', e);
      }
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to store payment method'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async upgradeTrial({ commit, dispatch }, { plan_id, token, currency }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const data = await subscriptionApi.upgradeTrial({ plan_id, token, currency });

      // Refresh subscription data after successful upgrade
      await dispatch('fetchCurrentSubscription');

      commit('SET_SUCCESS_MESSAGE', 'Trial upgraded successfully');
      return data;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to upgrade trial'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  }
  ,
  async fetchPaymentMethods({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const methods = await subscriptionApi.listPaymentMethods();
      commit('SET_PAYMENT_METHODS', methods);
      return methods;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to fetch payment methods'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async deletePaymentMethod({ commit }, paymentMethodId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const resp = await subscriptionApi.deletePaymentMethod(paymentMethodId);
      const methods = await subscriptionApi.listPaymentMethods();
      commit('SET_PAYMENT_METHODS', methods);
      return resp;
    } catch (error) {
      commit('SET_ERROR', translateApiError(error, 'Failed to delete payment method'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  }
};

export const getters = {
  plans: state => state.plans,
  currentSubscription: state => state.currentSubscription,
  usage: state => state.usage,
  paymentHistory: state => state.paymentHistory,
  paymentStatistics: state => state.paymentStatistics,
  paymentMethods: state => state.paymentMethods,
  isSubscribed: state => {
    if (!state.currentSubscription) {
      console.log('[isSubscribed] No subscription found');
      return false;
    }

    // Use is_valid which includes 24-hour grace period
    // is_valid checks: is_active && (no end_date || within 24 hours after end_date)
    if (state.currentSubscription.is_valid !== undefined && state.currentSubscription.is_valid !== null) {
      console.log('[isSubscribed] Using is_valid from API:', state.currentSubscription.is_valid);
      return state.currentSubscription.is_valid;
    }

    console.log('[isSubscribed] is_valid not available, using fallback logic');

    // Fallback for older API responses that don't have is_valid
    if (!state.currentSubscription.is_active) {
      console.log('[isSubscribed] Subscription not active');
      return false;
    }

    // Check if subscription has expired (including grace period)
    if (state.currentSubscription.end_date) {
      const now = new Date();
      const endDate = new Date(state.currentSubscription.end_date);
      // Add grace period
      const gracePeriodEnd = new Date(endDate.getTime() + (GRACE_PERIOD_HOURS * 60 * 60 * 1000));

      console.log('[isSubscribed] Checking grace period:', {
        now: now.toISOString(),
        endDate: endDate.toISOString(),
        gracePeriodEnd: gracePeriodEnd.toISOString(),
        isExpired: gracePeriodEnd <= now,
        is_in_grace_period: state.currentSubscription.is_in_grace_period
      });

      if (gracePeriodEnd <= now) {
        console.log('[isSubscribed] Grace period expired');
        return false;
      }
    }

    console.log('[isSubscribed] Subscription valid (active or in grace period)');
    return true;
  },
  isLoading: state => state.loading,
  hasError: state => !!state.error,
  error: state => state.error,
  tier: state => state.currentSubscription?.plan?.tier || 'free',
  subscriptionStatus: state => state.currentSubscription?.payment_status || null,
  // Helper getters for feature access
  canCreateCommunities: state => {
    const tier = state.currentSubscription?.plan?.tier || 'free';
    return {
      'free': 1,
      'basic': 3,
      'pro': 10,
      'enterprise': Infinity
    }[tier] || 0;
  },
  maxProductsPerCommunity: state => {
    const tier = state.currentSubscription?.plan?.tier || 'free';
    return {
      'free': 5,
      'basic': 20,
      'pro': 100,
      'enterprise': Infinity
    }[tier] || 0;
  },
  maxGroupsPerCommunity: state => {
    const tier = state.currentSubscription?.plan?.tier || 'free';
    return {
      'free': 3,
      'basic': 10,
      'pro': 50,
      'enterprise': Infinity
    }[tier] || 0;
  },
  hasAdvancedAnalytics: state => {
    const tier = state.currentSubscription?.plan?.tier || 'free';
    return ['pro', 'enterprise'].includes(tier);
  },
  hasAIRecommendations: state => {
    const tier = state.currentSubscription?.plan?.tier || 'free';
    return ['pro', 'enterprise'].includes(tier);
  },
  hasMessageScheduling: state => {
    const tier = state.currentSubscription?.plan?.tier || 'free';
    return ['basic', 'pro', 'enterprise'].includes(tier);
  },
  // Feature list generator for plan display
  getPlanFeatures: () => (plan) => {
    if (!plan || !plan.features) return [];
    
    // Handle both string array and object-based features
    if (Array.isArray(plan.features)) {
      // If features is a simple string array, display as is
      return plan.features.map((feature, index) => ({
        icon: 'mdi-check',
        label: feature,
        value: '✓'
      }));
    }
    
    // If features is an object with detailed feature flags
    const features = [
      {
        icon: 'mdi-account-group',
        label: 'Communities',
        value: plan.features.max_communities || '1'
      },
      {
        icon: 'mdi-account-multiple',
        label: 'Groups',
        value: plan.features.max_groups || '1'
      },
      {
        icon: 'mdi-whatsapp',
        label: 'WhatsApp Instances',
        value: plan.features.max_whatsapp_instances || '1'
      },
      {
        icon: 'mdi-chart-bar',
        label: 'AI Message Generations / month',
        value: plan.features.max_message_generations_per_month || '500'
      },
      {
        icon: 'mdi-chart-bar',
        label: 'Product Imports / month',
        value: plan.features.max_product_imports_per_month || '500'
      },
      {
        icon: 'mdi-pencil-box-outline',
        label: 'Custom AI Instructions',
        value: plan.features.custom_ai_instructions ? '✓' : '✗'
      },
      {
        icon: 'mdi-chart-bar',
        label: 'AI Message Generation',
        value: plan.features.message_generation ? '✓' : '✗'
      },
      {
        icon: 'mdi-package-variant-closed',
        label: 'Products per community',
        value: plan.features.max_products_per_community || '∞'
      },
      {
        icon: 'mdi-message-text',
        label: 'Messages per day',
        value: plan.features.max_messages_per_day === -1 ? '∞' : plan.features.max_messages_per_day
      },
      {
        icon: 'mdi-shopping',
        label: 'AliExpress Support',
        value: plan.features.aliexpress ? '✓' : '✗'
      },
      {
        icon: 'mdi-amazon',
        label: 'Amazon Support',
        value: plan.features.amazon ? '✓' : '✗'
      },
      {
        icon: 'mdi-telegram',
        label: 'Telegram Support',
        value: plan.features.telegram ? '✓' : '✗'
      },
      {
        icon: 'mdi-whatsapp',
        label: 'WhatsApp Support',
        value: plan.features.whatsapp ? '✓' : '✗'
      },
      {
        icon: 'mdi-truck-delivery',
        label: 'Order Tracking',
        value: plan.features.order_tracking ? '✓' : '✗'
      },
      {
        icon: 'mdi-chart-bar',
        label: 'Order Tracking Frequency',
        value: plan.features.order_tracking_frequency || 'daily'
      }
    ];
    
    return features;
  }
}; 